import { useEffect, useRef, useState } from "react";
import { functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";

import { niceAuthMode, partnerId } from "../partnerConfig";
import { useAuth } from "../AuthContext";
import SideScreen from "../components/SideScreen";
import { BlockButton, ArrowButton } from "../components/Buttons";
import PrivacyPolicy from "../components/privacy-policy";
import TermsOfService from "../components/terms-of-service";

import style from "../style/screens/Auth.module.css";

let count = 0;
const niceRequestStorageKey = `niceIntcRequestNo:${partnerId}`;
const niceAuthErrorMessage = "본인인증에 실패했습니다. 다시 시도해주세요.";

export const Auth = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const formRef = useRef<HTMLFormElement>(null);

    const [policy, setPolicy] = useState<boolean>(false);
    const [privacy, setPrivacy] = useState<boolean>(false);

    const refresh = () => {
        count++;
        if (count > 4) {
            window.location.href = `${window.location.pathname}?t=${Date.now()}`;
        }
    };

    const startLegacyNiceAuth = async () => {
        const result: any = await httpsCallable(functions, "getNiceApi")({ partnerId });
        const { token_version_id, enc_data, integrity_value } = result?.data?.data || {};
        const form = formRef.current;

        if (!token_version_id || !enc_data || !integrity_value || !form) {
            throw new Error("Legacy NICE auth response is invalid");
        }

        const setFormValue = (name: string, value: string) => {
            const input = form.elements.namedItem(name) as HTMLInputElement | null;
            if (!input) throw new Error(`Legacy NICE form field is missing: ${name}`);
            input.value = value;
        };

        setFormValue("token_version_id", token_version_id);
        setFormValue("enc_data", enc_data);
        setFormValue("integrity_value", integrity_value);
        form.submit();
    };

    const startIntegratedNiceAuth = async () => {
        const result: any = await httpsCallable(functions, "getNiceIntegratedAuthUrl")({ partnerId });
        const { auth_url, request_no } = result?.data?.data || {};

        if (!auth_url || !request_no) {
            throw new Error("Integrated NICE auth URL response is invalid");
        }

        sessionStorage.setItem(niceRequestStorageKey, request_no);
        window.location.href = auth_url;
    };

    const startNiceAuth = async () => {
        if (loading) return;

        sessionStorage.removeItem(niceRequestStorageKey);
        setError("");
        setLoading(true);

        try {
            if (niceAuthMode === "legacy") {
                await startLegacyNiceAuth();
            } else {
                await startIntegratedNiceAuth();
            }
        } catch (err) {
            console.error("Error at startNiceAuth: ", err);
            setError(niceAuthErrorMessage);
            setLoading(false);
        }
    };

    return (
        <>
            <div className={style["container"]}>
                <div className={style["unit--policy"]}>
                    <div onClick={refresh}>
                        <Logo />
                    </div>
                    <div className={style["wrapper--policy"]}>
                        <ArrowButton text="이용약관 보기" onClick={() => setPolicy(true)} />
                        <ArrowButton text="개인정보처리방침 보기" onClick={() => setPrivacy(true)} />
                    </div>
                    {error && <p className={style["text--auth-error"]} role="alert">{error}</p>}
                    <div className={style["wrapper"]}>
                        <BlockButton
                            text={error ? "다시 시도하기" : "전화번호로 시작하기"}
                            onClick={startNiceAuth}
                            loading={loading}
                        />
                    </div>
                </div>
            </div>

            <SideScreen appear={Boolean(policy)} title="하이비츠 이용약관" id="A1" close={() => setPolicy(false)}>
                <TermsOfService />
            </SideScreen>

            <SideScreen
                appear={Boolean(privacy)}
                title="하이비츠 개인정보처리방침"
                id="A2"
                close={() => setPrivacy(false)}
            >
                <PrivacyPolicy />
            </SideScreen>

            <form ref={formRef} action="https://nice.checkplus.co.kr/CheckPlusSafeModel/service.cb" method="get">
                <input type="hidden" name="m" value="service" />
                <input type="hidden" name="token_version_id" />
                <input type="hidden" name="enc_data" />
                <input type="hidden" name="integrity_value" />
            </form>
        </>
    );
};

export const Authenticate = () => {
    const { signInUser } = useAuth();
    const [error, setError] = useState<string>("");

    useEffect(() => {
        let mounted = true;

        const init = async () => {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                let result: any;
                const token_version_id = urlParams.get("token_version_id");
                const enc_data = urlParams.get("enc_data");
                const request_no = urlParams.get("request_no") || sessionStorage.getItem(niceRequestStorageKey);
                const web_transaction_id = urlParams.get("web_transaction_id");

                if (token_version_id && enc_data) {
                    result = await httpsCallable(
                        functions,
                        "authWithNiceApi"
                    )({ token_version_id, enc_data, partnerId });
                } else if (request_no && web_transaction_id) {
                    result = await httpsCallable(
                        functions,
                        "authWithNiceIntegratedAuth"
                    )({ request_no, web_transaction_id, partnerId });
                } else {
                    throw new Error(`NICE ${niceAuthMode} auth result is missing required parameters`);
                }
                const token = result?.data;

                if (!token) {
                    throw new Error("NICE auth did not return a Firebase token");
                }

                const credential = await signInUser(token);
                if (!credential) {
                    throw new Error("Firebase sign-in failed");
                }

                sessionStorage.removeItem(niceRequestStorageKey);
                window.location.replace("/");
            } catch (err) {
                console.error("Error at init at Authenticate: ", err);
                sessionStorage.removeItem(niceRequestStorageKey);
                if (mounted) setError(niceAuthErrorMessage);
            }
        };
        init();

        return () => {
            mounted = false;
        };
    }, []);

    const retry = () => {
        sessionStorage.removeItem(niceRequestStorageKey);
        window.location.replace("/");
    };

    return (
        <>
            <div className={style["container"]}>
                <div className={style["unit"]}>
                    <div className={style["container--logo"]}>
                        <Logo />
                        {error ? (
                            <p className={style["text--auth-error"]} role="alert">{error}</p>
                        ) : (
                            <>
                                <p className={style["text--float"]}>회원 인증 중입니다</p>
                                <span className={style["span--loading-bar"]}>
                                    <span></span>
                                </span>
                            </>
                        )}
                    </div>
                    {error && (
                        <div className={style["wrapper"]}>
                            <BlockButton text="다시 시도하기" onClick={retry} />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

// const Auth = () => {
//     const [login, setLogin] = useState<boolean>(false);
//     const [register, setRegister] = useState<boolean>(false);
//     const [page, setPage] = useState<"LOGIN" | "REGISTER" | null>(null);

//     useEffect(() => {
//         setLogin(page == "LOGIN");
//         setRegister(page == "REGISTER");
//     }, [page]);

//     // 뒤로가기 시 인증 에러 존재함
//     // useBackable("login", () => setPage(null), login);
//     // useBackable("register", () => setPage(null), register);

//     return (
//         <>
//             {!page && (
//                 <div className={style["container"]}>
//                     <div className={style["unit"]}>
//                         <div className={style["container--logo"]}>
//                             <Logo />
//                         </div>
//                         <div className={style["wrapper"]}>
//                             <BlockButton
//                                 text="회원가입 하기"
//                                 onClick={() => setPage("REGISTER")}
//                             />

//                             <BlockButton
//                                 type="Secondary"
//                                 text="로그인 하기"
//                                 onClick={() => setPage("LOGIN")}
//                             />
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {login && <Login setRegister={() => setPage("REGISTER")} />}
//             {register && <Register />}
//         </>
//     );
// };

// const Login = ({ setRegister }: { setRegister: () => void }) => {
//     const { user, signInUser, verifyCode } = useAuth();

//     const [title, setTitle] = useState<string>("휴대폰 번호를 입력해주세요");
//     const [loading, setLoading] = useState<boolean>(false);
//     const [disabled, setDisabled] = useState<boolean>(true);

//     const [phone, setPhone] = useState<string>("");
//     const [code, setCode] = useState<string>("");
//     const [codeErr, setCodeErr] = useState<string>("");

//     const phoneRef = useRef<HTMLInputElement | null>(null);
//     const codeRef = useRef<HTMLInputElement | null>(null);

//     const [step, setStep] = useState<number>(0);

//     // useEffect(() => {
//     //     phoneRef.current?.focus();
//     // }, []);

//     useEffect(() => {
//         if (step === 0) {
//             if (!/01[016789][^0][0-9]{2,3}[0-9]{3,4}/.test(phone)) {
//                 setDisabled(true);
//                 return;
//             }
//             setDisabled(!phone);
//         } else if (step === 1) {
//             if (code.length == 6) {
//                 setCodeErr("");
//                 setDisabled(!code);
//             }
//         }
//     }, [phone, code]);

//     useEffect(() => {
//         if (step === 1) {
//             setTitle("인증번호를 입력해주세요");
//             codeRef.current?.focus();
//         }

//         if (step > 0) {
//             setDisabled(true);
//         }
//     }, [step]);

//     const nextStep = async () => {
//         if (step == 0) {
//             sendCode();
//             return;
//         } else if (step == 1) {
//             verification();
//             return;
//         }
//     };

//     const sendCode = async () => {
//         setLoading(true);

//         return signInUser(phone)
//             .then(() => {
//                 setLoading(false);
//                 setStep(1);
//             })
//             .catch((err) => {
//                 console.error("Error at signInUser at nextStep", err);
//                 setLoading(false);
//                 return;
//             });
//     };

//     const verification = async () => {
//         setLoading(true);

//         return verifyCode(code)
//             .then(() => {
//                 setLoading(false);
//                 if (!user) {
//                     window.recaptchaVerifier = "";
//                     setRegister();
//                 }
//             })
//             .catch((err) => {
//                 console.error("Error at verifyCode at authenticate", err);
//                 setLoading(false);
//                 setCodeErr(ErrMsg[err.message]);
//                 if (err.message == "auth/invalid-verification-id") {
//                     sendCode();
//                 }

//                 return;
//             });
//     };

//     return (
//         <>
//             <div className={style["container--auth"]}>
//                 <h4 className={style["text"]}>{title}</h4>
//                 {step == 0 && (
//                     <div className={style["wrapper--inputs"]}>
//                         <LineInput
//                             ref={phoneRef}
//                             type="number"
//                             placeholder="휴대폰번호"
//                             maxLength={11}
//                             value={phone}
//                             setValue={(v) => setPhone(v)}
//                         />
//                     </div>
//                 )}
//                 {step == 1 && (
//                     <div className={style["wrapper--inputs"]}>
//                         <LineInput
//                             ref={codeRef}
//                             type="number"
//                             placeholder="인증번호"
//                             maxLength={6}
//                             value={code}
//                             error={codeErr}
//                             setValue={(v) => setCode(v)}
//                         />
//                     </div>
//                 )}
//             </div>

//             <FooterButton
//                 text="확인"
//                 onClick={() => nextStep()}
//                 loading={loading}
//                 disabled={disabled}
//             />
//         </>
//     );
// };

// const Register = () => {
//     const { signInUser, verifyCode } = useAuth();

//     const [title, setTitle] = useState<string>("이름을 입력해주세요");
//     const [loading, setLoading] = useState<boolean>(false);
//     const [disabled, setDisabled] = useState<boolean>(true);
//     const [inputDisabled, setInputDisabled] = useState<{
//         [key: string]: boolean;
//     }>({
//         name: false,
//         // securityNum1: false,
//         // securityNum2: false,
//         phone: false,
//         code: false,
//     });

//     const [name, setName] = useState<string>("");
//     // const [securityNum1, setSecurityNum1] = useState<string>("");
//     // const [securityNum2, setSecurityNum2] = useState<string>("");
//     const [phone, setPhone] = useState<string>("");
//     const [code, setCode] = useState<string>("");

//     const [codeErr, setCodeErr] = useState<string>("");

//     const [step, setStep] = useState<number>(0);

//     const nameRef = useRef<HTMLInputElement | null>(null);
//     // const securityNum1Ref = useRef<HTMLInputElement | null>(null);
//     // const securityNum2Ref = useRef<HTMLInputElement | null>(null);
//     const phoneRef = useRef<HTMLInputElement | null>(null);
//     const codeRef = useRef<HTMLInputElement | null>(null);

//     const [policy, setPolicy] = useState<boolean>(false);
//     const [privacy, setPrivacy] = useState<boolean>(false);

//     // useEffect(() => {
//     //     nameRef.current?.focus();
//     // }, []);

//     useEffect(() => {
//         if (step === 0) {
//             setDisabled(!name);
//         } else if (step === 1) {
//             // setDisabled(
//             //     !(securityNum1.length === 6 && securityNum2.length > 0)
//             // );
//             // if (securityNum1.length == 6) securityNum2Ref.current?.focus();
//         } else if (step === 2) {
//             if (!/01[016789][^0][0-9]{2,3}[0-9]{3,4}/.test(phone)) {
//                 setDisabled(true);
//                 return;
//             }
//             setDisabled(!phone);
//         } else if (step === 3) {
//             if (code.length == 6) {
//                 setCodeErr("");
//                 setDisabled(!code);
//             }
//         }
//     }, [name, phone, code]);

//     useEffect(() => {
//         if (step === 1) {
//             setInputDisabled((prev) => ({ ...prev, name: true }));
//             setTitle("주민등록번호를 입력해주세요");
//             // securityNum1Ref.current?.focus();
//         } else if (step === 2) {
//             // setInputDisabled((prev) => ({ ...prev, securityNum1: true }));
//             // setInputDisabled((prev) => ({ ...prev, securityNum2: true }));
//             setTitle("휴대폰번호를 입력해주세요");
//             phoneRef.current?.focus();
//         } else if (step === 3) {
//             setInputDisabled((prev) => ({ ...prev, phone: true }));
//             setTitle("인증번호를 입력해주세요");
//             codeRef.current?.focus();
//         }

//         if (step > 0) {
//             setDisabled(true);
//         }
//     }, [step]);

//     const nextStep = async () => {
//         if (step == 2) {
//             sendCode();
//             return;
//         } else if (step == 3) {
//             verification();
//             return;
//         }

//         if (step == 0) {
//             setStep(2);
//         } else {
//             setStep(step + 1);
//         }
//     };

//     const sendCode = async () => {
//         setLoading(true);

//         return signInUser(phone)
//             .then(() => {
//                 setLoading(false);
//                 setStep(3);
//             })
//             .catch((err) => {
//                 console.error("Error at signInUser at nextStep", err);
//                 setLoading(false);
//                 return;
//             });
//     };

//     const verification = async () => {
//         setLoading(true);

//         return verifyCode(code, true)
//             .then(() => {
//                 setLoading(false);
//                 setStep(4);
//             })
//             .catch((err) => {
//                 console.error("Error at verifyCode at authenticate", err);
//                 setLoading(false);

//                 switch (err.message) {
//                     case "auth/invalid-verification-code":
//                         setCodeErr(ErrMsg[err.message]);
//                         break;
//                 }

//                 return;
//             });
//     };

//     const createUser = async () => {
//         setLoading(true);

//         return httpsCallable(
//             functions,
//             "userCreate"
//         )({
//             name: name,
//             // securityNum1: securityNum1,
//             // securityNum2: securityNum2,
//             phone: phone,
//         })
//             .then(async (result: any) => {
//                 console.log(result);

//                 return signInWithCustomToken(auth, result.data.token).catch(
//                     (err) => {
//                         console.error(err);
//                     }
//                 );
//             })
//             .catch((err) => {
//                 console.error(err);
//             });
//     };

//     return (
//         <>
//             {/* <div id="" style={{ display: "none" }} /> */}
//             {step < 4 ? (
//                 <>
//                     <div className={style["container--auth"]}>
//                         <h4 className={style["text"]}>{title}</h4>
//                         {step < 3 && (
//                             <div className={style["wrapper--inputs"]}>
//                                 <LineInput
//                                     ref={nameRef}
//                                     placeholder="이름"
//                                     disabled={inputDisabled["name"]}
//                                     value={name}
//                                     setValue={(v) => setName(v)}
//                                     cancelBtn={true}
//                                 />
//                                 {/* {step > 0 && (
//                                     <div className={style["unit--securityNum"]}>
//                                         <LineInput
//                                             ref={securityNum1Ref}
//                                             type="number"
//                                             placeholder="주민번호"
//                                             maxLength={6}
//                                             disabled={
//                                                 inputDisabled["securityNum1"]
//                                             }
//                                             value={securityNum1}
//                                             setValue={(v) => setSecurityNum1(v)}
//                                         />
//                                         <span
//                                             className={
//                                                 style["span--security-dash"]
//                                             }
//                                         ></span>
//                                         <div
//                                             className={
//                                                 style["unit--securityNum2"]
//                                             }
//                                         >
//                                             <LineInput
//                                                 ref={securityNum2Ref}
//                                                 type="number"
//                                                 placeholder=""
//                                                 maxLength={1}
//                                                 disabled={
//                                                     inputDisabled[
//                                                         "securityNum2"
//                                                     ]
//                                                 }
//                                                 value={securityNum2}
//                                                 align="center"
//                                                 setValue={(v) =>
//                                                     setSecurityNum2(v)
//                                                 }
//                                             />
//                                             <svg
//                                                 width="116"
//                                                 height="16"
//                                                 viewBox="0 0 116 16"
//                                                 fill="#AAAAAA"
//                                                 xmlns="http://www.w3.org/2000/svg"
//                                             >
//                                                 <circle cx="8" cy="8" r="8" />
//                                                 <circle cx="28" cy="8" r="8" />
//                                                 <circle cx="48" cy="8" r="8" />
//                                                 <circle cx="68" cy="8" r="8" />
//                                                 <circle cx="88" cy="8" r="8" />
//                                                 <circle cx="108" cy="8" r="8" />
//                                             </svg>
//                                         </div>
//                                     </div>
//                                 )} */}
//                                 {step > 1 && (
//                                     <LineInput
//                                         ref={phoneRef}
//                                         type="number"
//                                         placeholder="휴대폰번호"
//                                         maxLength={11}
//                                         disabled={inputDisabled["phone"]}
//                                         value={phone}
//                                         setValue={(v) => setPhone(v)}
//                                     />
//                                 )}
//                             </div>
//                         )}
//                         {step == 3 && (
//                             <div className={style["wrapper--inputs"]}>
//                                 <LineInput
//                                     ref={codeRef}
//                                     type="number"
//                                     placeholder="인증번호"
//                                     maxLength={6}
//                                     disabled={inputDisabled["code"]}
//                                     value={code}
//                                     error={codeErr}
//                                     setValue={(v) => setCode(v)}
//                                 />
//                             </div>
//                         )}
//                     </div>

//                     <FooterButton
//                         text="확인"
//                         onClick={() => nextStep()}
//                         loading={loading}
//                         disabled={disabled}
//                     />
//                 </>
//             ) : (
//                 <>
//                     <div className={style["container"]}>
//                         <div className={style["unit--policy"]}>
//                             <Logo />
//                             <h4
//                                 className={[
//                                     style["text"],
//                                     style["text--paddingTop"],
//                                 ].join(" ")}
//                             >
//                                 {name}님 환영합니다!
//                             </h4>

//                             <div className={style["wrapper--policy"]}>
//                                 <ArrowButton
//                                     text="이용약관 보기"
//                                     onClick={() => setPolicy(true)}
//                                 />
//                                 <ArrowButton
//                                     text="개인정보처리방침 보기"
//                                     onClick={() => setPrivacy(true)}
//                                 />
//                             </div>

//                             <div className={style["wrapper"]}>
//                                 <BlockButton
//                                     text="전체 약관에 동의하기"
//                                     loading={loading}
//                                     onClick={createUser}
//                                 />
//                             </div>
//                         </div>
//                     </div>
//                 </>
//             )}

//             <SideScreen
//                 appear={Boolean(policy)}
//                 title="하이비츠 이용약관"
//                 id="A1"
//                 close={() => setPolicy(false)}
//             >
//                 <TermsOfService />
//             </SideScreen>

//             <SideScreen
//                 appear={Boolean(privacy)}
//                 title="하이비츠 개인정보처리방침"
//                 id="A2"
//                 close={() => setPrivacy(false)}
//             >
//                 <PrivacyPolicy />
//             </SideScreen>
//         </>
//     );
// };

const Logo = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="60" height="80" viewBox="0 0 60 80" fill="var(--color-brand)">
            <path d="M22.6305 0V36.9524C25.6799 33.7619 28.4704 31.5143 31.0019 30.2C33.5335 28.8857 36.1705 28.219 38.9035 28.219C42.3844 28.219 45.3666 29.2 47.8407 31.1714C50.3147 33.1429 51.9545 35.4476 52.7696 38.1048C53.5846 40.7619 53.9874 45.2381 53.9874 51.5238V68.019C53.9874 72.2286 54.3805 74.8571 55.1765 75.8952C55.9724 76.9333 57.5738 77.6 59.9903 77.8762V80H31.8362V77.8762C33.8116 77.6 35.2979 76.819 36.2952 75.5143C37.0048 74.4952 37.3596 71.9905 37.3596 68.019V48.4571C37.3596 43.8952 37.1774 41.0381 36.8226 39.8762C36.3815 38.4286 35.5472 37.381 34.339 36.7333C33.6294 36.3524 32.8431 36.181 32.0376 36.1905C30.4841 36.2095 28.9786 36.7524 27.5019 37.8095C27.2046 38.0286 26.8977 38.2762 26.5813 38.5524C24.0018 40.8952 22.6305 44.2857 22.6305 47.7619V68.019C22.6305 71.9524 22.9278 74.4095 23.5223 75.3905C24.2703 76.7238 25.8141 77.5524 28.1539 77.8667V79.9905H-0.000145847V77.8667C2.33963 77.6286 3.99858 76.8857 4.98627 75.6286C5.65752 74.7619 5.99314 72.2286 5.99314 68.019V11.981C5.99314 7.80952 5.60957 5.21905 4.83284 4.19048C4.05611 3.17143 2.44512 2.49524 -0.00973511 2.18095V0H22.6209H22.6305Z" />
            <path d="M45.1846 3.14288C47.6011 3.14288 49.6436 3.98098 51.3217 5.66669C52.9903 7.34288 53.8341 9.37145 53.8341 11.7334C53.8341 14.0953 52.9903 16.1048 51.293 17.7715C49.6052 19.4286 47.5627 20.2667 45.1846 20.2667C42.8065 20.2667 40.7831 19.4381 39.105 17.7715C37.4365 16.1143 36.5926 14.0953 36.5926 11.7334C36.5926 9.37145 37.4269 7.35241 39.105 5.66669C40.7735 3.9905 42.8065 3.14288 45.1846 3.14288Z" />
        </svg>
    );
};

export default Auth;
